
interface ImpressionData {
  t: string // type
  i: number // id
  v: number // views
  s: number // seconds
}

interface ActiveImpression {
  type: string
  id: number
  startTime: number
  lastVisibleTime: number
  totalSeconds: number
}

export function useImpressions() {
  
  const { $api } = useNuxtApp()
  
  // Constants
  const STORAGE_KEY = 'impressions_data'
  const ACTIVE_KEY = 'active_impressions'
  const SEND_INTERVAL = 5000
  const VISIBILITY_THRESHOLD = 0.1

  // Local state - safe for SSR
  let observer: IntersectionObserver | null = null
  let activeImpressions = new Map<string, ActiveImpression>()
  let sendTimer: NodeJS.Timeout | null = null
  let isTabVisible = true
  let isInitialized = false
  let debugMode = false

  const debugLog = (message: string, ...args: any[]): void => {
    if (debugMode) {
      console.log(`[Impressions Debug] ${message}`, ...args)
    }
  }

  const getStorageData = (): ImpressionData[] => {
    if (typeof localStorage === 'undefined') return []
    try {
      const data = localStorage.getItem(STORAGE_KEY)
      return data ? JSON.parse(data) : []
    } catch {
      return []
    }
  }

  const saveStorageData = (data: ImpressionData[]): void => {
    if (typeof localStorage === 'undefined') return
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } catch {}
  }

  const getActiveImpressions = (): Map<string, ActiveImpression> => {
    if (typeof localStorage === 'undefined') return new Map()
    try {
      const data = localStorage.getItem(ACTIVE_KEY)
      if (!data) return new Map()
      const parsed = JSON.parse(data)
      return new Map(Object.entries(parsed))
    } catch {
      return new Map()
    }
  }

  const saveActiveImpressions = (): void => {
    if (typeof localStorage === 'undefined') return
    try {
      const obj = Object.fromEntries(activeImpressions)
      localStorage.setItem(ACTIVE_KEY, JSON.stringify(obj))
    } catch {}
  }

  const updateActiveImpression = (key: string, impression: ActiveImpression): void => {
    const now = Date.now()
    if (isTabVisible && impression.lastVisibleTime > 0) {
      impression.totalSeconds += Math.round((now - impression.lastVisibleTime) / 1000)
      debugLog(`Updated impression for ${key}: +${Math.round((now - impression.lastVisibleTime) / 1000)}s, total: ${impression.totalSeconds}s`)
    }
    impression.lastVisibleTime = isTabVisible ? now : 0
    activeImpressions.set(key, impression)
    saveActiveImpressions()
  }

  const finalizeImpression = (key: string): void => {
    const impression = activeImpressions.get(key)
    if (!impression) return

    updateActiveImpression(key, impression)
    debugLog(`Finalizing impression for ${key}: ${impression.totalSeconds}s total`)
    
    if (impression.totalSeconds > 0) {
      const stored = getStorageData()
      const existing = stored.find(item => item.t === impression.type && item.i === impression.id)
      
      if (existing) {
        existing.v += 1
        existing.s += impression.totalSeconds
        debugLog(`Updated existing record: ${impression.type}:${impression.id} - Views: ${existing.v}, Seconds: ${existing.s}`)
      } else {
        stored.push({
          t: impression.type,
          i: impression.id,
          v: 1,
          s: impression.totalSeconds
        })
        debugLog(`Created new record: ${impression.type}:${impression.id} - Views: 1, Seconds: ${impression.totalSeconds}`)
      }
      
      saveStorageData(stored)
    }
    
    activeImpressions.delete(key)
    saveActiveImpressions()
  }

  const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
    const now = Date.now()
    debugLog(`Intersection change detected: ${entries.length} elements`)
    
    entries.forEach(entry => {
      const element = entry.target as HTMLElement
      const type = element.dataset.iType
      const idStr = element.dataset.iId
      const ignore = element.dataset.iIgnore
      
      debugLog(`Processing element: type=${type}, id=${idStr}, ignore=${ignore}, intersecting=${entry.isIntersecting}, ratio=${entry.intersectionRatio}`)
      
      if (!type || !idStr) {
        debugLog(`Skipping element - missing data attributes`)
        return
      }
      
      if (ignore === 'true') {
        debugLog(`Skipping element - marked as ignored`)
        return
      }
      
      const id = parseInt(idStr)
      const key = `${type}-${id}`
      
      if (entry.isIntersecting) {
        if (!activeImpressions.has(key)) {
          activeImpressions.set(key, {
            type,
            id,
            startTime: now,
            lastVisibleTime: isTabVisible ? now : 0,
            totalSeconds: 0
          })
          saveActiveImpressions()
          debugLog(`Started tracking: ${key} (visible: ${isTabVisible}, ratio: ${entry.intersectionRatio})`)
        } else {
          const impression = activeImpressions.get(key)!
          if (impression.lastVisibleTime === 0 && isTabVisible) {
            impression.lastVisibleTime = now
            activeImpressions.set(key, impression)
            saveActiveImpressions()
            debugLog(`Resumed tracking: ${key} (ratio: ${entry.intersectionRatio})`)
          } else {
            debugLog(`Element ${key} already being tracked (lastVisible: ${impression.lastVisibleTime})`)
          }
        }
      } else {
        debugLog(`Element ${key} not intersecting (intersecting: ${entry.isIntersecting}, ratio: ${entry.intersectionRatio})`)
        const impression = activeImpressions.get(key)
        if (impression) {
          if (impression.lastVisibleTime > 0) {
            updateActiveImpression(key, impression)
            debugLog(`Paused tracking: ${key}`)
          }
          
          // Если элемент полностью исчез с экрана (ratio = 0) - финализируем
          if (entry.intersectionRatio === 0 && !entry.isIntersecting) {
            finalizeImpression(key)
            debugLog(`Finalized impression: ${key}`)
          }
        }
      }
    })
    
    debugLog(`Active impressions count: ${activeImpressions.size}`)
  }

  const handleVisibilityChange = (): void => {
    const wasVisible = isTabVisible
    isTabVisible = !document.hidden
    const now = Date.now()
    
    debugLog(`Tab visibility changed: ${wasVisible ? 'visible' : 'hidden'} -> ${isTabVisible ? 'visible' : 'hidden'}`)
    
    if (wasVisible && !isTabVisible) {
      debugLog('Tab became hidden - pausing all tracking')
      activeImpressions.forEach((impression, key) => {
        if (impression.lastVisibleTime > 0) {
          updateActiveImpression(key, impression)
        }
      })
    } else if (!wasVisible && isTabVisible) {
      debugLog('Tab became visible - resuming all tracking')
      activeImpressions.forEach((impression, key) => {
        impression.lastVisibleTime = now
        activeImpressions.set(key, impression)
      })
      saveActiveImpressions()
    }
  }

  const handleBeforeUnload = (): void => {
    debugLog(`Page unloading - finalizing ${activeImpressions.size} active impressions`)
    activeImpressions.forEach((_, key) => {
      finalizeImpression(key)
    })
    sendData()
  }

  const sendData = async (): Promise<void> => {
    const data = getStorageData()
    if (data.length === 0) {
      debugLog('No data to send')
      return
    }

    // Преобразуем данные в правильный формат если нужно
    const formattedData = data.map(item => {
      // Если данные уже в новом формате
      if ('t' in item && 'i' in item) {
        return item
      }
      // Если данные в старом формате - преобразуем
      return {
        t: (item as any).type || item.t,
        i: (item as any).id || item.i,
        v: item.v,
        s: item.s
      }
    })

    debugLog(`Sending ${formattedData.length} impression records:`, formattedData)
    
    try {
      await $api.postImpressionsStat(formattedData)
      saveStorageData([])
      debugLog('Successfully sent impressions data')
    } catch (error) {
      debugLog('Failed to send impressions:', error)
      console.warn('Failed to send impressions:', error)
    }
  }

  const startSendTimer = (): void => {
    if (sendTimer) clearInterval(sendTimer)
    sendTimer = setInterval(sendData, SEND_INTERVAL)
    debugLog(`Started send timer: ${SEND_INTERVAL}ms interval`)
  }

  const observeElements = (): void => {
    if (typeof window === 'undefined' || observer) return
    
    observer = new IntersectionObserver(handleIntersection, {
      threshold: 0,
      rootMargin: '0px'
    })

    const elements = document.querySelectorAll('[data-i-type][data-i-id]:not([data-i-ignore="true"])')
    elements.forEach(element => observer!.observe(element))
    debugLog(`Started observing ${elements.length} existing elements`)

    const mutationObserver = new MutationObserver(mutations => {
      let addedCount = 0
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element
            if (element.hasAttribute('data-i-type') && element.hasAttribute('data-i-id') && element.getAttribute('data-i-ignore') !== 'true') {
              observer!.observe(element)
              addedCount++
            }
            const descendants = element.querySelectorAll('[data-i-type][data-i-id]:not([data-i-ignore="true"])')
            descendants.forEach(desc => {
              observer!.observe(desc)
              addedCount++
            })
          }
        })
      })
      if (addedCount > 0) {
        debugLog(`Started observing ${addedCount} new elements`)
      }
    })

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    })
    debugLog('Mutation observer started for dynamic elements')
  }

  const init = (): void => {
    if (typeof window === 'undefined' || isInitialized) return
    
    isInitialized = true
    activeImpressions = getActiveImpressions()
    debugLog(`Initialized impressions tracking. Restored ${activeImpressions.size} active impressions`)
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)
    
    observeElements()
    startSendTimer()
    debugLog('Impressions tracking fully initialized')
  }

  const cleanup = (): void => {
    debugLog('Cleaning up impressions tracking')
    
    if (observer) {
      observer.disconnect()
      observer = null
      debugLog('Intersection observer disconnected')
    }
    
    if (sendTimer) {
      clearInterval(sendTimer)
      sendTimer = null
      debugLog('Send timer cleared')
    }
    
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('beforeunload', handleBeforeUnload)
    debugLog('Event listeners removed')
    
    activeImpressions.forEach((_, key) => {
      finalizeImpression(key)
    })
    debugLog('All active impressions finalized')
    
    isInitialized = false
  }


  const toggleDebug = (enabled?: boolean): boolean => {
    debugMode = enabled !== undefined ? enabled : !debugMode
    debugLog(`Debug mode ${debugMode ? 'enabled' : 'disabled'}`)
    return debugMode
  }

  const clearStorage = (): void => {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(ACTIVE_KEY)
      debugLog('Cleared all impression storage')
    }
  }

  return {
    init,
    cleanup,
    sendData,
    getStorageData,
    toggleDebug,
    clearStorage,
    activeImpressions: readonly(activeImpressions)
  }
}